// The session-LIST queries, moved from `src/hooks/query/useQuerySessions.ts`
// (v1) and `useQuerySessionsV2.ts` (v2) at ARCFE-1003. Both old paths are now
// delegates.
//
// Shape: the D3 `useQuerySessionById` W3c split. What moved is the data —
// cache key, fetch, retry/refetch policy, page-param arithmetic, and the
// success/failure logging. What did NOT move is the REVIEW-CONTEXT
// COMPOSITION: whether this user may read another member's list is a
// six-input app decision (auth token, current user, team details, support
// role, allowlisted review access, the selected review member, plus desktop
// online-readiness and the list-version atom). The delegate resolves all of
// it and passes the results in, exactly as the legacy hooks computed them.
//
// `fetchTeamMemberSessions` is INJECTED rather than imported: its endpoint
// (`GET /teams/:t/members/:m/sessions`) is tagged `@domain workspace` in the
// hub, and re-tagging it is a cross-lane adjudication a carve PR must not
// make on its own. The port keeps this family moving without touching that
// claim.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { logger, sanitizeError } from '@heidi/runtime/logger';

import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
  GET_SESSIONS,
  GET_TEAM_MEMBER_SESSIONS,
  type SessionsResponse,
} from '../../contracts';
import { getSessions } from '../api/list';

/** Refetch/retry policy, moved verbatim from `@/constants/sessions`. */
export const SESSIONS_REFETCH_INTERVAL = 30 * 1000;
export const SESSIONS_QUERY_RETRY = 3;

export type TeamMemberSessionsFetcher = (input: {
  teamId: string;
  memberId: string;
  pageSize?: number;
  lastSessionCreatedAt?: string | null;
}) => Promise<{ data: SessionsResponse }>;

type NextPageParam = { lastSessionCreatedAt: string | null | undefined };

/**
 * Shared by both infinite lists: stop at the last page, otherwise page from
 * the final row's `created_time_local`. Moved verbatim — the cursor is the
 * timestamp, not an offset.
 */
const getNextPageParam = (lastPage: SessionsResponse): NextPageParam | undefined => {
  if (!lastPage.has_next_page) {
    return undefined;
  }
  // `sessions` is required by the local contract but optional in the generated
  // schema, so a page can arrive without it.
  const lastSessionCreatedAt = lastPage.sessions?.at(-1)?.created_time_local;
  // No cursor means the next request would repeat the first page forever, so
  // treat the list as exhausted instead.
  if (!lastSessionCreatedAt) {
    return undefined;
  }
  return { lastSessionCreatedAt };
};

const logged = async <T>(fetch: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const { data } = await fetch();
    logger.info('[SessionList] Loaded sessions successfully');
    return data;
  } catch (error) {
    logger.error('[SessionList] Failed to load sessions', sanitizeError(error));
    throw error;
  }
};

// ---------------------------------------------------------------------------
// v1 — flat lists
// ---------------------------------------------------------------------------

export function useQueryAdminSessionsV1({
  retryDelay,
  enabled,
}: {
  retryDelay: (attemptIndex: number) => number;
  /** Fully resolved by the delegate: token + user + desktop-ready + version. */
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [GET_SESSIONS],
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    staleTime: 0, // set to 0 to ensure we refetch on window focus
    retry: SESSIONS_QUERY_RETRY,
    retryDelay,
    enabled,
    queryFn: async () => logged(() => getSessions({})),
  });
}

export function useQueryTeamMemberSessionsV1({
  teamId,
  memberId,
  fetchTeamMemberSessions,
  retryDelay,
  enabled,
}: {
  teamId: string | undefined;
  memberId: string | undefined;
  fetchTeamMemberSessions: TeamMemberSessionsFetcher;
  retryDelay: (attemptIndex: number) => number;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: [GET_TEAM_MEMBER_SESSIONS, { teamId }, { memberId }],
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    staleTime: 0,
    retry: SESSIONS_QUERY_RETRY,
    retryDelay,
    enabled: enabled && !!teamId && !!memberId,
    queryFn: async () => {
      // Preserved verbatim: the guard returns undefined rather than throwing,
      // so a race that clears the review target settles empty, not errored.
      if (!!teamId && !!memberId) {
        return logged(() => fetchTeamMemberSessions({ teamId, memberId }));
      }
    },
  });
}

// ---------------------------------------------------------------------------
// v2 — infinite lists
// ---------------------------------------------------------------------------

export function useQueryAdminSessionsV2({
  pageSize,
  retryDelay,
  enabled,
}: {
  pageSize: number;
  retryDelay: (attemptIndex: number) => number;
  enabled: boolean;
}) {
  return useInfiniteQuery({
    queryKey: [GET_INFINITE_SESSIONS, pageSize],
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    staleTime: 0,
    retry: SESSIONS_QUERY_RETRY,
    retryDelay,
    enabled,
    queryFn: async ({ pageParam }: { pageParam: NextPageParam }) =>
      logged(() =>
        getSessions({
          pageSize: pageSize ? Number(pageSize) : undefined,
          lastSessionCreatedAt: pageParam.lastSessionCreatedAt ?? undefined,
        })
      ),
    getNextPageParam,
    initialPageParam: { lastSessionCreatedAt: undefined } as NextPageParam,
  });
}

export function useQueryTeamMemberSessionsV2({
  teamId,
  memberId,
  pageSize,
  fetchTeamMemberSessions,
  retryDelay,
  enabled,
}: {
  teamId: string | undefined;
  memberId: string | undefined;
  pageSize: number;
  fetchTeamMemberSessions: TeamMemberSessionsFetcher;
  retryDelay: (attemptIndex: number) => number;
  enabled: boolean;
}) {
  return useInfiniteQuery({
    queryKey: [GET_INFINITE_TEAM_MEMBER_SESSIONS, { teamId }, { memberId }, pageSize],
    refetchInterval: SESSIONS_REFETCH_INTERVAL,
    staleTime: 0,
    retry: SESSIONS_QUERY_RETRY,
    retryDelay,
    enabled: enabled && !!teamId && !!memberId,
    queryFn: async ({ pageParam }: { pageParam: NextPageParam }) => {
      // Preserved verbatim, and DIFFERENT from v1's guard: the infinite query
      // must return a page-shaped value, so it yields an empty page rather
      // than undefined.
      if (!teamId || !memberId) {
        // Preserved BYTE-IDENTICAL from the pre-move hook, including the
        // absent `count`. The cast is deliberate: adding `count: 0` to
        // satisfy `SessionsResponse` would change the payload a consumer
        // reads on an empty page, and a move PR never changes behaviour.
        return {
          sessions: [],
          total_count: 0,
          has_next_page: false,
        } as unknown as SessionsResponse;
      }
      return logged(() =>
        fetchTeamMemberSessions({
          teamId,
          memberId,
          pageSize: pageSize ? Number(pageSize) : undefined,
          lastSessionCreatedAt: pageParam.lastSessionCreatedAt ?? undefined,
        })
      );
    },
    getNextPageParam,
    initialPageParam: { lastSessionCreatedAt: undefined } as NextPageParam,
  });
}
